import type { Draft } from 'immer';
import { produce } from 'immer';
import { includes, isFunction } from 'lodash-es';
import { createLogic } from 'redux-logic';
import { v4 as uuid } from 'uuid';

import { FEATURE_FLAGS_STORAGE_KEY } from 'common/featureFlags/featureFlags';
import { setLanguage } from 'common/i18n/i18n';
import { CILogger } from 'common/log/log.provider';
import { clearFxMsulTokenCache } from 'common/rest/fx';
import { updateEconsentSignature } from 'module/econsent/utils/endpoints';

import { CIAnalytics } from '../analytics/analytics.provider';
import { AuthUserType } from '../graphql/types';

import { AuthActions, AuthActionType } from './actions';
import type { LogoutRequestOptions, SetActiveUserFromHandoffPayload } from './actions';
import { AuthLocalStorageKeys } from './constants';
import {
    EMPTY_FORGOT_CREDENTIALS,
    persistForgotCredentials,
    readPersistedForgotCredentials,
} from './forgotCredentialsPersistence';
import {
    addGraphQLDataToUser,
    buildAuthUserFromCognitoSession,
    changePassword,
    clearPersistedIdentity,
    deleteLastPagesOpened,
    determineSelectedActiveStudySite,
    getGraphQLClient,
    login,
    logLogin,
    logLogout,
    mapGraphQLToRespondent,
    markPasswordUsed,
    persistAuthUsers,
    resetUserData,
    revokeCognitoTokenAndSignOut,
    signOutOfOkta,
    updateActiveStudySite,
    updateUsersLastLoggedInTime,
    userChangePassword,
} from './helpers';
import { clearPostLoginRedirect } from './postLoginRedirect';
import { userMustAcceptPrivacyPolicy } from './privacyPolicyAcceptance';
import { updateUserToSPP } from './queries';
import type { ActiveStudySite, AuthState, AuthUser } from './types';

function getDefaultState(): AuthState {
    const defaultState: AuthState = {
        activeMeeting: false,
        thumbnailMode: false,
        activeUser: null,
        submitted: false,
        complete: false,
        error: null,
        activeStudySite: {
            activeStudy: null,
            activeSite: null,
            activeStudySiteReady: false,
        },
        meetingDetails: {
            id: '',
            name: '',
            isAdmin: false,
        },
        passwordChangeStatus: 'IDLE',
        newPasswordRequested: false,
        passwordResetUserId: null,
        ssoLoading: false,
        userRoleSwitchPending: false,
        sessionEndedByLogout: false,
        forgotCredentials: readPersistedForgotCredentials(),
    };

    const activeUser = localStorage.getItem('activeUser');
    const activeStudySite = localStorage.getItem(AuthLocalStorageKeys.activeStudySite);

    if (activeUser) {
        defaultState.activeUser = JSON.parse(activeUser);
    }
    if (activeStudySite) {
        defaultState.activeStudySite = JSON.parse(activeStudySite);
    }

    return defaultState;
}

function clearExternalSessions(): void {
    try {
        if (window.pendo && isFunction(window.pendo.clearSession)) {
            window.pendo.clearSession();
        }

        window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession?.();
    } catch (error) {
        CILogger.getInstance().warn('Clearing external sessions at logout failed', error);
    }
}

function modifyAuthStateToActiveUserSession(
    draft: Draft<AuthState>,
    user: AuthUser,
    activeStudySite: ActiveStudySite
): void {
    localStorage.removeItem('sessionLastActivity');
    draft.activeUser = { ...user, sessionId: uuid() };
    draft.error = null;
    draft.activeStudySite = activeStudySite;
    draft.submitted = false;
    draft.complete = true;
    draft.sessionEndedByLogout = false;
    persistAuthUsers(draft);
}

export const authReducers = (state = getDefaultState(), action): AuthState => {
    switch (action.type) {
        case AuthActionType.HANDLE_PASSWORD_CHANGE_STATUS:
            return produce(state, (newState) => {
                newState.passwordChangeStatus = action.payload;

                return newState;
            });
        case AuthActionType.RESET_ACTIVE_STUDY_SITE:
            return produce(state, (newState) => {
                newState.activeStudySite = {
                    activeStudy: null,
                    activeSite: null,
                    activeStudySiteReady: false,
                };
                localStorage.setItem(AuthLocalStorageKeys.activeStudySite, JSON.stringify(newState.activeStudySite));

                return newState;
            });
        case AuthActionType.UPDATE_ACTIVE_STUDY_SITE:
            return produce(state, (newState) => {
                const { study, site, updatedUser } = action.payload;
                const selectedActiveStudySite: ActiveStudySite = {
                    activeStudy: study,
                    activeSite: site,
                    activeStudySiteReady: true,
                };
                newState.activeStudySite = selectedActiveStudySite;
                if (updatedUser) {
                    newState.activeUser = updatedUser;
                    persistAuthUsers(newState);
                } else {
                    updateActiveStudySite(newState);
                }
                CIAnalytics.getInstance().identifyUser(state.activeUser);

                return newState;
            });
        case AuthActionType.UPDATE_ACTIVE_USER_PASSCODE_STATE:
            return produce(state, (newState) => {
                if (!newState.activeUser) {
                    return newState;
                }

                const { passcodeStatus, passcodeUpdatedAt, passcodeAttempts } = action.payload;

                newState.activeUser.passcodeStatus = passcodeStatus;
                newState.activeUser.passcodeUpdatedAt = passcodeUpdatedAt;
                newState.activeUser.passcodeAttempts = passcodeAttempts;

                persistAuthUsers(newState);

                return newState;
            });
        case AuthActionType.LOGIN_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.LOGIN_SUCCESS:
            return produce(state, (newState): void => {
                const authUser: AuthUser = action.payload;

                modifyAuthStateToActiveUserSession(newState, authUser, determineSelectedActiveStudySite(authUser));
                newState.passwordResetUserId = null;
                newState.forgotCredentials = EMPTY_FORGOT_CREDENTIALS;

                logLogin(newState);
            });
        case AuthActionType.LOGIN_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.LOGOUT_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
                newState.sessionEndedByLogout = true;
            });
        case AuthActionType.RESET_USER_DATA:
            return produce(state, (newState): void => {
                newState.submitted = true;
            });
        case AuthActionType.RESET_COMPLETE:
            return produce(state, (newState): void => {
                newState.complete = false;
            });
        case AuthActionType.LOGOUT_SUCCESS:
            return produce(state, (newState): void => {
                newState.activeUser = null;
                newState.submitted = false;
                newState.complete = true;
                newState.activeStudySite = { activeStudy: null, activeSite: null, activeStudySiteReady: true };
                newState.ssoLoading = true;
                newState.passwordResetUserId = null;
                newState.forgotCredentials = EMPTY_FORGOT_CREDENTIALS;
            });
        case AuthActionType.LOG_OUT_PRESERVING_DEVICE_HANDOFF_STATE:
            return produce(state, (newState): void => {
                newState.ssoLoading = true;
            });
        case AuthActionType.SET_ACTIVE_USER_FROM_HANDOFF:
            return produce(state, (newState): void => {
                const { user, activeStudySite } = action.payload as SetActiveUserFromHandoffPayload;

                modifyAuthStateToActiveUserSession(newState, user, activeStudySite);
                newState.ssoLoading = user.authenticationMethod === 'OKTA';

                logLogin(newState);
            });
        case AuthActionType.POLICY_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.POLICY_SUCCESS:
            return produce(state, (newState): void => {
                const { activeUser } = action.payload;
                newState.activeUser = activeUser;
                newState.submitted = false;
                newState.complete = true;
                persistAuthUsers(newState);
            });
        case AuthActionType.POLICY_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CHANGE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.forgotCredentials = EMPTY_FORGOT_CREDENTIALS;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }
            });
        case AuthActionType.CHANGE_PASS_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CREATE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                const { authUser } = action.payload as { authUser: AuthUser; newPassword: string };
                newState.activeUser = { ...authUser, sessionId: uuid() };

                newState.submitted = false;
                newState.complete = false;
                newState.forgotCredentials = EMPTY_FORGOT_CREDENTIALS;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }
            });
        case AuthActionType.CREATE_PASS_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.USER_CHANGE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                const { authUser, oldPassword, newPassword } = action.payload as {
                    authUser: AuthUser;
                    oldPassword: string;
                    newPassword: string;
                };
                newState.activeUser = { ...authUser, sessionId: uuid() };

                if (state.passwordChangeStatus !== 'IDLE') {
                    newState.passwordChangeStatus = 'SUCCESS';
                }
                newState.submitted = false;
                newState.complete = false;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }

                // Update Econsent Signature
                updateEconsentSignature(authUser.pk, oldPassword, newPassword);

                persistAuthUsers(newState);
            });
        case AuthActionType.USER_CHANGE_PASS_FAILURE:
            return produce(state, (newState): void => {
                if (state.passwordChangeStatus !== 'IDLE') {
                    newState.passwordChangeStatus = 'FAILED';
                }
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CLEAR_ERROR:
            return produce(state, (newState): void => {
                newState.error = null;
            });
        case AuthActionType.USER_UPDATE_SECURITY_QUESTIONS: {
            const { securityQuestion1Text, securityQuestion2Text, securityQuestion3Text } = action.payload;

            return produce(state, (newState): void => {
                newState.activeUser.securityQuestion1Text = securityQuestion1Text;
                newState.activeUser.securityQuestion2Text = securityQuestion2Text;
                newState.activeUser.securityQuestion3Text = securityQuestion3Text;
                persistAuthUsers(newState);
            });
        }
        case AuthActionType.TELEVISIT_REQUEST: {
            const { data, active } = action.payload;

            return produce(state, (newState): void => {
                newState.activeMeeting = active;
                newState.meetingDetails.id = data.id || '';
                newState.meetingDetails.name = data.name || '';
                newState.meetingDetails.isAdmin = data.isAdmin || false;
                if (!active) {
                    newState.thumbnailMode = false;
                }
            });
        }
        case AuthActionType.TELEVISIT_THUMBNAIL_MODE:
            return produce(state, (newState): void => {
                newState.thumbnailMode = !state.thumbnailMode;
            });
        case AuthActionType.UPDATE_PATIENT_STUDY_ID:
            return produce(state, (newState): void => {
                const studyId = action.payload.data;
                newState.activeUser.studyId = studyId;
                newState.activeUser.userStudies[0].studyId = studyId;
                if (newState.activeStudySite.activeStudy.studyId) {
                    newState.activeStudySite.activeStudy.studyId = studyId;
                }
                if (newState.activeStudySite.activeSite.studyId) {
                    newState.activeStudySite.activeSite.studyId = studyId;
                }
                persistAuthUsers(newState);
            });
        case AuthActionType.UPDATE_PATIENT_ARM_ID:
            return produce(state, (newState): void => {
                const armId = action.payload.data;
                newState.activeUser.armId = armId;
                persistAuthUsers(newState);
            });
        case AuthActionType.UPDATE_PATIENT_CLOCK_FORMAT:
            return produce(state, (newState): void => {
                newState.activeUser.preferredClockFormat = action.payload.data;
                persistAuthUsers(newState);
            });
        case AuthActionType.ACTIVATION_PASSWORD_REQUEST:
            return produce(state, (newState): void => {
                newState.newPasswordRequested = action.payload.data;
            });
        case AuthActionType.SET_PASSWORD_RESET_USER_ID:
            return produce(state, (newState): void => {
                newState.passwordResetUserId = action.payload.userId;
            });
        case AuthActionType.SET_SSO_LOADING:
            return produce(state, (newState) => {
                newState.ssoLoading = action.payload.data;
            });
        case AuthActionType.USER_ROLE_SWITCH_PENDING:
            return produce(state, (newState) => {
                newState.userRoleSwitchPending = action.payload.isPending;
            });
        case AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_FOUND:
            return produce(state, (newState): void => {
                newState.forgotCredentials.username = action.payload.username;
                newState.forgotCredentials.securityQuestions = action.payload.securityQuestions;
                newState.forgotCredentials.activationTargetUsername = action.payload.username;
            });
        case AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_LOADED_FOR_RESET_LINK:
            return produce(state, (newState): void => {
                newState.forgotCredentials.securityQuestions = action.payload.securityQuestions;
            });
        case AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_NEEDED:
            return produce(state, (newState): void => {
                newState.forgotCredentials.activationTargetUsername = action.payload.username;
            });
        case AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_CODE_ISSUED:
            return produce(state, (newState): void => {
                newState.forgotCredentials.activationCode = action.payload.activationCode;
            });
        case AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_DISCARDED:
            return produce(state, (newState): void => {
                newState.forgotCredentials.username = null;
                newState.forgotCredentials.securityQuestions = null;
            });
        case AuthActionType.FORGOT_CREDENTIALS_CLEARED:
            return produce(state, (newState): void => {
                newState.forgotCredentials = EMPTY_FORGOT_CREDENTIALS;
            });
        default:
            return state;
    }
};

const LoginLogic = createLogic({
    type: AuthActionType.LOGIN_REQUEST,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const { username, password, ssoTokens } = action.payload;
        login(username, password, ssoTokens, dispatch)
            .then((authUser) => deleteLastPagesOpened(authUser))
            .then((authUser) => updateUsersLastLoggedInTime(authUser))
            .then((authUser): void => {
                setLanguage(authUser?.preferredLanguage || 'en-US');
                // to avoid showing guides in policy page, check if the current policy and user accepted policy are same
                if (!userMustAcceptPrivacyPolicy(authUser)) {
                    CIAnalytics.getInstance().identifyUser(authUser);
                }
                dispatch(AuthActions.loginSucceeded(authUser));
            })
            .catch((err): void => {
                dispatch(AuthActions.loginFailed(err));
            })
            .finally(done);
    },
});

const ResetUserDataLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.RESET_USER_DATA,
    process({ getState }, dispatch, done): void {
        const authUser = getState()?.auth?.activeUser;

        resetUserData(authUser)
            .then(() => {
                // TODO: there should be a better / cleaner approach to reset data
                window.location.href = '/';
            })
            .catch(() => {
                dispatch(AuthActions.logoutRequest());
            })
            .finally(() => {
                done();
            });
    },
});

function clearUserSessionCaches(): void {
    CIAnalytics.getInstance().clearSession();
    clearFxMsulTokenCache();
}

const SESSION_STORAGE_KEYS_KEPT_ON_LOGOUT = [FEATURE_FLAGS_STORAGE_KEY];

function clearSessionStorageOnLogout(): void {
    for (const key of Object.keys(sessionStorage)) {
        if (!includes(SESSION_STORAGE_KEYS_KEPT_ON_LOGOUT, key)) {
            sessionStorage.removeItem(key);
        }
    }
}

async function endUserSession(authState: AuthState): Promise<void> {
    logLogout(authState);
    clearExternalSessions();
    await CILogger.getInstance().flush();
    await revokeCognitoTokenAndSignOut(authState.activeUser);
    clearUserSessionCaches();
    clearSessionStorageOnLogout();
    clearPersistedIdentity();
}

function hardReloadToRootAfterCognitoLogout(): void {
    window.location.href = '/';
}

async function returnToLoginScreen(isOkta: boolean, endOktaSessionLocally: boolean): Promise<void> {
    if (isOkta) {
        await signOutOfOkta({ endOktaSessionLocally });
    } else {
        hardReloadToRootAfterCognitoLogout();
    }
}

function ensureLoggedOut(
    dispatch: (action: ReturnType<typeof AuthActions.logoutSucceeded>) => void,
    signOutRedirect: () => Promise<void>,
    done: () => void
): void {
    dispatch(AuthActions.logoutSucceeded());
    signOutRedirect()
        .catch((error) => CILogger.getInstance().error('Logout sign-out failed', error))
        .finally(done);
}

const LogoutLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.LOGOUT_REQUEST,
    process({ getState, action }, dispatch, done): void {
        const { endOktaSessionLocally = false } = (action as { payload?: LogoutRequestOptions }).payload ?? {};
        const authState = getState().auth;
        const isOkta = authState.activeUser?.authenticationMethod === 'OKTA';
        endUserSession(authState)
            .catch((error) => {
                CILogger.getInstance().error('Logout teardown failed', error);

                return CILogger.getInstance().flush();
            })
            .finally(() => {
                clearPostLoginRedirect();
                ensureLoggedOut(dispatch, () => returnToLoginScreen(isOkta, endOktaSessionLocally), done);
            });
    },
});

const logOutPreservingDeviceHandoffStateLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.LOG_OUT_PRESERVING_DEVICE_HANDOFF_STATE,
    process({ getState }, dispatch, done): void {
        const authState = getState().auth;
        const isOkta = authState.activeUser?.authenticationMethod === 'OKTA';
        endUserSession(authState)
            .catch((error) => {
                CILogger.getInstance().error('Device-handoff logout teardown failed', error);

                return CILogger.getInstance().flush();
            })
            .finally(() => {
                ensureLoggedOut(
                    dispatch,
                    async () => {
                        if (isOkta) {
                            await signOutOfOkta();
                        }
                    },
                    done
                );
            });
    },
});

/**
 * Responsible for creating password for new users
 */
const ChangePassLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.CHANGE_PASS,
    process({ action, getState }, dispatch, done): void {
        // @ts-ignore
        const { newPassword } = action.payload;
        changePassword(
            newPassword,
            getState().auth.passwordResetUserId,
            getState().auth.forgotCredentials.activationCode
        )
            .then((authUser): void => {
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                CIAnalytics.getInstance().identifyUser(authUser);

                dispatch(AuthActions.loginSucceeded(authUser));
            })
            .catch((err): void => {
                dispatch(AuthActions.changePasswordFailed(err));
            })
            .finally(done);
    },
});

const UserChangePassLogic = createLogic({
    type: AuthActionType.USER_CHANGE_PASS,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const { oldPassword, newPassword } = action.payload;
        userChangePassword(oldPassword, newPassword)
            .then((authUser): void => {
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                dispatch(AuthActions.userChangePasswordSucceeded(authUser, oldPassword, newPassword));
            })
            .catch((err): void => {
                dispatch(AuthActions.userChangePasswordFailed(err));
            })
            .finally(done);
    },
});

const CreatePassLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.CREATE_PASSWORD,
    process({ action, getState }, dispatch, done): void {
        // @ts-ignore
        const { newPassword } = action.payload;
        changePassword(
            newPassword,
            getState().auth.passwordResetUserId,
            getState().auth.forgotCredentials.activationCode
        )
            .then((authUser): void => {
                CIAnalytics.getInstance().identifyUser(authUser);
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                dispatch(AuthActions.createPasswordSucceeded(authUser, newPassword));
            })
            .catch((err): void => {
                dispatch(AuthActions.createPasswordFailed(err));
            })
            .finally(done);
    },
});

const PolicyLogic = createLogic({
    type: AuthActionType.POLICY_REQUEST,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const data = action.payload;
        const { activeUser } = data;
        getGraphQLClient()
            .mutate({
                mutation: updateUserToSPP,
                variables: {
                    userId: activeUser?.pk,
                    pVersion: activeUser.currentPolicy?.[0]?.majorVersion,
                },
            })
            .then((): void => {
                dispatch(AuthActions.policySucceeded(data));
            })
            .catch((err): void => {
                dispatch(AuthActions.policyFailed(err));
            })
            .then(() => done());
    },
});

const PersistForgotCredentialsLogic = createLogic({
    type: [
        AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_FOUND,
        AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_LOADED_FOR_RESET_LINK,
        AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_NEEDED,
        AuthActionType.FORGOT_CREDENTIALS_ACTIVATION_CODE_ISSUED,
        AuthActionType.FORGOT_CREDENTIALS_QUESTIONS_DISCARDED,
        AuthActionType.FORGOT_CREDENTIALS_CLEARED,
        AuthActionType.CREATE_PASS_SUCCESS,
        AuthActionType.CHANGE_PASS_SUCCESS,
        AuthActionType.LOGIN_SUCCESS,
        AuthActionType.LOGOUT_SUCCESS,
    ],
    process({ getState }, _dispatch, done): void {
        persistForgotCredentials((getState() as { auth: AuthState }).auth.forgotCredentials);
        done();
    },
});

export const ReduxLogic = [
    LoginLogic,
    ChangePassLogic,
    PolicyLogic,
    UserChangePassLogic,
    CreatePassLogic,
    LogoutLogic,
    logOutPreservingDeviceHandoffStateLogic,
    ResetUserDataLogic,
    PersistForgotCredentialsLogic,
];

/**
 * For unit test use only
 */
export const UnderTest = {
    buildAuthUserFromCognitoSession,
    mapGraphQLToUser: addGraphQLDataToUser,
    AuthActions,
    authReducers,
    AuthActionType,
    mapGraphQLToRespondent,
};
